import { useMemo } from 'react';

type FilterConfig<T> = {
  key: string;
  getValue: (item: T) => string | string[] | undefined;
};

export function useCrossFilterCounts<T>(
  items: T[],
  filters: Record<string, string[]>,
  filterConfigs: FilterConfig<T>[],
) {
  // Calculate counts for each filter
  const counts = useMemo(() => {
    // Helper to apply all filters except one
    function applyFiltersExcept(items: T[], exceptKey: string) {
      return items.filter((item) =>
        filterConfigs.every((config) => {
          if (config.key === exceptKey) return true;
          const filterVals = filters[config.key] ?? [];
          // Fix: treat "all" as no filter
          if (filterVals.length === 0 || (filterVals.length === 1 && filterVals[0] === 'all')) return true;
          const value = config.getValue(item);
          if (Array.isArray(value)) {
            return value.some((v) => filterVals.includes(v));
          }
          return filterVals.includes(value as string);
        }),
      );
    }

    const result: Record<string, Record<string, number>> = {};
    for (const config of filterConfigs) {
      const filtered = applyFiltersExcept(items, config.key);
      const count: Record<string, number> = {};
      filtered.forEach((item) => {
        const value = config.getValue(item);
        if (Array.isArray(value)) {
          value.forEach((v) => (count[v] = (count[v] ?? 0) + 1));
        } else if (value) {
          count[value] = (count[value] ?? 0) + 1;
        }
      });
      result[config.key] = count;
    }
    return result;
  }, [items, filterConfigs, filters]);

  return counts;
}
